import { useState, useRef, useEffect } from "react";
import type { SseHotelMessage, HotelSearchItem } from "@/api/aiChat";
import { INITIAL_SHOW_COUNT, LAZY_LOAD_STEP } from "./train-card";
import { stripHtml } from "@/utils/format";

// ════════════════════════════════════════════════════
//  酒店列表卡片（大图卡片式布局 + 懒加载）
//  字段对应后端 data.hotels[]：
//    hotel_name / cover_image / star_text / star_rating / contact_phone
//    min_price / room_count / has_available / intro / address / district / distance
// ════════════════════════════════════════════════════

/** 星级五角星图标（0-5，未知返回空）——实心橙色五角星 */
function StarDiamonds({ count }: { count: number }) {
  if (!count || count <= 0) return null;
  return (
    <span className="inline-flex items-center gap-px">
      {Array.from({ length: Math.min(count, 5) }).map((_, i) => (
        <i key={i} className="ri-star-fill" style={{ fontSize: 9, color: "#F59E0B" }} />
      ))}
    </span>
  );
}

/** 价格显示（min_price=0 视为暂无报价） */
function fmtPrice(min_price?: number | string): string | null {
  if (min_price == null || min_price === "") return null;
  const n = typeof min_price === "number" ? min_price : parseFloat(String(min_price));
  if (!isFinite(n) || n <= 0) return null;
  return `¥${n.toLocaleString()}`;
}

/** 距离格式化 */
function fmtDistance(d?: number): string | null {
  if (d == null || !isFinite(d) || d <= 0) return null;
  return d >= 1000 ? `${(d / 1000).toFixed(1)}km` : `${Math.round(d)}m`;
}

/** 从 intro 中提取「开业 / 装修 / 客房数」摘要 */
function introHighlight(intro?: string): { open?: string; renovate?: string; rooms?: string } {
  if (!intro) return {};
  const out: { open?: string; renovate?: string; rooms?: string } = {};
  const openM = intro.match(/开业[：:]\s*(\d{4})/);
  if (openM) out.open = openM[1];
  const renM = intro.match(/装修[：:]\s*(\d{4})/);
  if (renM) out.renovate = renM[1];
  const roomM = intro.match(/客房数[：:]\s*(\d+)/);
  if (roomM) out.rooms = roomM[1];
  return out;
}

interface HotelQueryRaw {
  city?: string;
  place?: string;
  total?: number;
  check_in?: string;
  check_out?: string;
  hotels: HotelSearchItem[];
}

export default function HotelCard({
  data,
  onHotelSelect,
}: {
  data: SseHotelMessage | HotelQueryRaw;
  onHotelSelect?: (hotel: HotelSearchItem) => void;
}) {
  const hd = ("data" in data && (data as SseHotelMessage).data)
    ? (data as SseHotelMessage).data
    : (data as HotelQueryRaw);

  // ★ 懒加载状态（复用常量）
  const [visibleCount, setVisibleCount] = useState(INITIAL_SHOW_COUNT);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const hotels = hd?.hotels ?? [];
  const showHotels = hotels.slice(0, visibleCount);
  const isAllLoaded = visibleCount >= hotels.length;

  // ★ 展开简介的酒店 id 集合
  const [expanded, setExpanded] = useState<Set<string | number>>(new Set());
  const toggleIntro = (key: string | number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || isAllLoaded) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisibleCount((prev) => Math.min(prev + LAZY_LOAD_STEP, hotels.length));
      },
      { rootMargin: "80px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isAllLoaded, hotels.length]);

  if (!hotels.length) return null;

  /** 是否有任意一家酒店可订（用于顶部提示） */
  const anyAvailable = hotels.some((h) => h.has_available || (h.room_count ?? 0) > 0);

  return (
    <div className="flex flex-col gap-0 rounded-xl w-full overflow-hidden"
      style={{ background: "#FAFBFF", border: "1px solid #EEF0F6" }}
    >
      {/* 标题栏 */}
      <div
        className="flex items-center gap-2 px-3.5 py-2.5 flex-wrap"
        style={{ background: "linear-gradient(135deg, #F5F3FF, #EDE9FE)", borderBottom: "1px solid #EEF0F6" }}
      >
        <i className="ri-hotel-bed-line" style={{ color: "#8B5CF6", fontSize: 15 }} />
        <span className="text-[12px] font-semibold" style={{ color: "#333" }}>
          {hd.city || hd.place || "酒店推荐"}
        </span>
        <span className="text-[10px] px-2 py-0.5 rounded-full font-mono"
          style={{ background: "#DDD6FE", color: "#7C3AED" }}
        >
          共 {hd.total ?? hotels.length} 家
        </span>
        {!isAllLoaded && (
          <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: "#DDD6FE", color: "#7C3AED" }}>
            已显示 {visibleCount}/{hotels.length}
          </span>
        )}
      </div>

      {/* 入离日期条 + 无房提示 */}
      {(hd.check_in || hd.check_out) && (
        <div className="flex items-center gap-2 px-3.5 py-2 flex-wrap text-[10px]"
          style={{ background: "#FBFAFF", borderBottom: "1px solid #F0F2F8" }}
        >
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium"
            style={{ background: "#EDE9FE", color: "#6D28D9" }}
          >
            <i className="ri-calendar-line" style={{ fontSize: 9 }} />
            {hd.check_in || "—"} 至 {hd.check_out || "—"}
          </span>
          {!anyAvailable && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium"
              style={{ background: "#FEF2F2", color: "#DC2626" }}
            >
              <i className="ri-error-warning-line" style={{ fontSize: 9 }} />
              该时段暂无房
            </span>
          )}
        </div>
      )}

      {/* 酒店列表 */}
      <div className="flex flex-col">
        {showHotels.map((hotel: HotelSearchItem, idx: number) => {
          const name = stripHtml(hotel.hotel_name || hotel.name || "未知酒店");
          const cover = hotel.cover_image || hotel.thumb || hotel.photos?.[0];
          const price = fmtPrice(hotel.min_price ?? hotel.price);
          const distance = fmtDistance(hotel.distance);
          const starText = hotel.star_text && hotel.star_text !== "未知" ? hotel.star_text : "";
          const phone = hotel.contact_phone || hotel.tel;
          const key = hotel.id ?? name;
          const isOpen = expanded.has(key);
          const info = introHighlight(hotel.intro);
          // 该酒店是否有房
          const available = hotel.has_available || (hotel.room_count ?? 0) > 0;

          return (
            <div
              key={`${key}-${idx}`}
              className={`px-3 py-3 ${onHotelSelect ? "cursor-pointer active:scale-[0.995]" : "active:bg-[#F5F3FF]"}`}
              style={{
                transition: "background 0.15s, transform 0.1s",
                borderBottom: idx < showHotels.length - 1 ? "1px solid #F0F2F8" : "none",
              }}
              onClick={() => onHotelSelect?.(hotel)}
            >
              <div className="flex gap-2.5">
                {/* ★ 封面大图 */}
                <div className="w-[88px] h-[88px] rounded-lg overflow-hidden flex-shrink-0 relative"
                  style={{ background: "#F1F5F9", border: "1px solid #EDE9FE" }}
                >
                  {cover ? (
                    <img
                      src={cover}
                      alt={name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      /* ★ 携程 CDN 有防盗链，去掉 Referer 才能加载 */
                      referrerPolicy="no-referrer"
                      crossOrigin="anonymous"
                      onError={(e) => {
                        const img = e.target as HTMLImageElement;
                        img.style.display = "none";
                        const parent = img.parentElement;
                        if (parent && !parent.querySelector(".cover-fallback")) {
                          const fb = document.createElement("div");
                          fb.className = "cover-fallback absolute inset-0 flex items-center justify-center";
                          fb.innerHTML = '<i class="ri-hotel-bed-line" style="font-size:22px;color:#C4B5FD"></i>';
                          parent.appendChild(fb);
                        }
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <i className="ri-hotel-bed-line" style={{ fontSize: 22, color: "#C4B5FD" }} />
                    </div>
                  )}
                </div>

                {/* 信息区 */}
                <div className="flex-1 min-w-0 flex flex-col">
                  {/* 第一行：名称 */}
                  <div className="flex items-start gap-1.5">
                    <span className="text-[13px] font-bold leading-snug flex-1"
                      style={{
                        color: "#1E293B",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                      title={name}
                    >
                      {name}
                    </span>
                  </div>

                  {/* 第二行：星级 + 档次标签 + 距离 */}
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    {starText && (
                      <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded flex-shrink-0"
                        style={{ background: "#FEF3C7", color: "#B45309", border: "1px solid #FDE68A" }}
                      >
                        <StarDiamonds count={hotel.star_rating ?? 0} />
                        {starText}
                      </span>
                    )}
                    {distance && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded flex-shrink-0"
                        style={{ background: "#F1F5F9", color: "#64748B" }}
                      >
                        <i className="ri-map-pin-2-line" style={{ fontSize: 8 }} /> {distance}
                      </span>
                    )}
                  </div>

                  {/* 第三行：地址 */}
                  {(hotel.district || hotel.address) && (
                    <div className="flex items-center gap-1 mt-1 text-[10px] min-w-0 overflow-hidden" style={{ color: "#94A3B8" }}>
                      <i className="ri-map-pin-line flex-shrink-0" style={{ fontSize: 10, color: "#CBD5E1" }} />
                      <span className="truncate min-w-0 flex-1">
                        {[stripHtml(hotel.district), stripHtml(hotel.address)].filter(Boolean).join(" · ")}
                      </span>
                    </div>
                  )}

                  {/* 第四行：开业/装修/客房数 + 电话 */}
                  {(info.open || info.renovate || info.rooms || phone) && (
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap text-[9px] overflow-hidden" style={{ color: "#94A3B8" }}>
                      {info.open && <span>{info.open}年开业</span>}
                      {info.renovate && <span>{info.renovate}年装修</span>}
                      {info.rooms && <span>{info.rooms}间客房</span>}
                      {phone && (
                        <span className="inline-flex items-center gap-0.5 truncate min-w-0" style={{ maxWidth: "100%" }}>
                          <i className="ri-phone-line flex-shrink-0" style={{ fontSize: 9 }} />{stripHtml(phone)}
                        </span>
                      )}
                    </div>
                  )}

                  {/* 第五行：价格 / 房态 */}
                  <div className="flex items-end justify-between gap-2 mt-auto pt-1.5 min-w-0">
                    {price ? (
                      <span className="flex items-baseline gap-0.5 flex-shrink-0">
                        <span className="text-[15px] font-bold" style={{ color: "#DC2626" }}>{price}</span>
                        <span className="text-[9px]" style={{ color: "#9CA3AF" }}>起</span>
                      </span>
                    ) : (
                      <span className="text-[10px] px-1.5 py-0.5 rounded flex-shrink-0"
                        style={{ background: "#F1F5F9", color: "#94A3B8" }}
                      >
                        暂无报价
                      </span>
                    )}
                    <span className="text-[9px] px-1.5 py-0.5 rounded flex-shrink-0 whitespace-nowrap"
                      style={available
                        ? { background: "#F0FDF4", color: "#16A34A" }
                        : { background: "#FEF2F2", color: "#DC2626" }}
                    >
                      {available ? `有房${hotel.room_count ? ` ${hotel.room_count}间` : ""}` : "暂无房"}
                    </span>
                  </div>
                </div>
              </div>

              {/* ★ 简介（可展开/收起） */}
              {hotel.intro && (
                <div className="mt-2">
                  <div className="text-[10px] leading-relaxed"
                    style={{
                      color: "#64748B",
                      display: isOpen ? "block" : "-webkit-box",
                      WebkitLineClamp: isOpen ? undefined : 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {stripHtml(hotel.intro)}
                  </div>
                  {hotel.intro.length > 60 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleIntro(key); }}
                      className="text-[9px] mt-0.5 cursor-pointer"
                      style={{ color: "#8B5CF6" }}
                    >
                      {isOpen ? "收起" : "展开详情"}
                      <i className={`ri-arrow-${isOpen ? "up" : "down"}-s-line ml-0.5`} style={{ fontSize: 9 }} />
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* 懒加载哨兵 */}
        {!isAllLoaded && (
          <div ref={sentinelRef} className="flex items-center justify-center py-3">
            <div className="flex items-center gap-1.5 text-[10px]" style={{ color: "#AAA" }}>
              <i className="ri-loader-4-line animate-spin" style={{ fontSize: 12 }} />
              <span>向下滑动加载更多...</span>
            </div>
          </div>
        )}

        {/* 全部加载完毕 */}
        {isAllLoaded && hotels.length > INITIAL_SHOW_COUNT && (
          <div className="flex items-center justify-center py-2" style={{ borderTop: "1px solid #F0F2F8" }}>
            <span className="text-[10px]" style={{ color: "#C0C8D4" }}>
              — 已展示全部 {hotels.length} 家酒店 —
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
